import React, { useState, useEffect } from 'react';
import { useCurrency } from '../../hooks/useCurrency';
import { Home, Search, Download, Printer } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface CashbookEntry {
  id: string;
  transaction_date: string;
  transaction_time: string;
  description: string;
  cash_in: number;
  cash_out: number;
  balance: number;
  payment_method: string;
  reference_number?: string;
  created_by: string;
}

export default function DailyCashbook() {
  const { formatCurrency } = useCurrency();
  const [entries, setEntries] = useState<CashbookEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    loadCashbookEntries();
  }, [selectedDate]);

  const loadCashbookEntries = async () => {
    try {
      setLoading(true);
      
      const url = import.meta.env.VITE_SUPABASE_URL;
      if (!url || url === 'https://placeholder.supabase.co') {
        setEntries(getMockCashbookEntries());
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('daily_cashbook')
        .select('*')
        .eq('transaction_date', selectedDate)
        .order('transaction_time', { ascending: true });

      if (error) throw error;
      setEntries(data || []);
    } catch (error) {
      console.error('Error loading cashbook entries:', error);
      setEntries(getMockCashbookEntries());
    } finally {
      setLoading(false);
    }
  };

  const getMockCashbookEntries = (): CashbookEntry[] => {
    return [
      {
        id: '1',
        transaction_date: '2025-01-27',
        transaction_time: '09:00:00',
        description: 'Opening Balance',
        cash_in: 100000,
        cash_out: 0,
        balance: 100000,
        payment_method: 'cash',
        reference_number: 'CB-OPEN-001',
        created_by: 'Admin',
      },
      {
        id: '2',
        transaction_date: '2025-01-27',
        transaction_time: '10:30:00',
        description: 'Patient consultation fees',
        cash_in: 15000,
        cash_out: 0,
        balance: 115000,
        payment_method: 'cash',
        reference_number: 'CB-2025-001',
        created_by: 'Receptionist',
      },
      {
        id: '3',
        transaction_date: '2025-01-27',
        transaction_time: '11:45:00',
        description: 'Medical supplies purchase',
        cash_in: 0,
        cash_out: 8000,
        balance: 107000,
        payment_method: 'cash',
        reference_number: 'CB-2025-002',
        created_by: 'Admin',
      },
      {
        id: '4',
        transaction_date: '2025-01-27',
        transaction_time: '14:20:00',
        description: 'Lab test fees',
        cash_in: 12000,
        cash_out: 0,
        balance: 119000,
        payment_method: 'cash',
        reference_number: 'CB-2025-003',
        created_by: 'Receptionist',
      },
      {
        id: '5',
        transaction_date: '2025-01-27',
        transaction_time: '16:00:00',
        description: 'Utility bill payment',
        cash_in: 0,
        cash_out: 5000,
        balance: 114000,
        payment_method: 'cash',
        reference_number: 'CB-2025-004',
        created_by: 'Admin',
      },
    ];
  };

  const filteredEntries = entries.filter(entry => {
    const matchesSearch = 
      entry.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.reference_number?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const totalCashIn = filteredEntries.reduce((sum, entry) => sum + entry.cash_in, 0);
  const totalCashOut = filteredEntries.reduce((sum, entry) => sum + entry.cash_out, 0);
  const closingBalance = filteredEntries.length > 0 ? filteredEntries[filteredEntries.length - 1].balance : 0;

  const handleExportCSV = () => {
    const headers = ['Time', 'Description', 'Cash In', 'Cash Out', 'Balance', 'Reference', 'Created By'];
    const rows = filteredEntries.map(entry => [
      entry.transaction_time,
      entry.description,
      entry.cash_in,
      entry.cash_out,
      entry.balance,
      entry.reference_number || 'N/A',
      entry.created_by,
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `daily-cashbook-${selectedDate}.csv`;
    a.click();
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Home className="w-4 h-4" />
            <span>/</span>
            <span>Billings/Reception</span>
            <span>/</span>
            <span className="text-gray-900 font-medium">Daily Cashbook</span>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleExportCSV}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              <span>Export CSV</span>
            </button>
            <button
              onClick={handlePrint}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Printer className="w-4 h-4" />
              <span>Print</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-none px-6 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-600">Total Cash In</p>
            <p className="text-2xl font-bold text-green-600 mt-1">{formatCurrency(totalCashIn)}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-600">Total Cash Out</p>
            <p className="text-2xl font-bold text-red-600 mt-1">{formatCurrency(totalCashOut)}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-600">Closing Balance</p>
            <p className="text-2xl font-bold text-teal-600 mt-1">{formatCurrency(closingBalance)}</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search entries..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cash In
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cash Out
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Balance
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Reference
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                      Loading...
                    </td>
                  </tr>
                ) : filteredEntries.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                      No cashbook entries found
                    </td>
                  </tr>
                ) : (
                  filteredEntries.map((entry) => (
                    <tr key={entry.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {entry.transaction_time}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {entry.description}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">
                        {entry.cash_in > 0 ? `৳${entry.cash_in.toLocaleString()}` : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-medium">
                        {entry.cash_out > 0 ? `৳${entry.cash_out.toLocaleString()}` : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                        {formatCurrency(entry.balance)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {entry.reference_number || '-'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
